import {
  CursorData,
  CursorParams,
  ToAttachmentParams,
  UploadAttachmentData,
  WithProfilesData,
  WithProfilesParams,
} from '../../api/utils';
import { UserId, Uuid } from '../../models/Base';
import {
  RewardCategoryJson,
  RewardOfferJson,
  RewardRedemptionJson,
  RewardPlaform,
  ReferralSystem,
  RewardReferralJson,
  RewardReferralCode,
} from '../../models/RewardStore';
import {
  ModifiedPick,
  Optional,
  PartialPick,
  RequiredPick,
} from '../../models/Utils';
import restClient, { RestRequestOptions, RestResponse } from './';

interface RewardCategoriesData {
  rewardCategories: RewardCategoryJson[];
}
export const getRewardCategories = (
  path: { store: RewardPlaform },
  params: CursorParams = {},
  options?: RestRequestOptions,
): Promise<RestResponse<RewardCategoriesData & CursorData>> => {
  return restClient.get(`/rewards/${path.store}/categories`, params, options);
};

interface RewardCategoryData {
  rewardCategory: RewardCategoryJson;
}
export const createRewardCategory = (
  path: { store: RewardPlaform },
  body: RequiredPick<RewardCategoryJson, 'i18nRewardCategoryInfo'>,
  options?: RestRequestOptions,
): Promise<RestResponse<RewardCategoryData>> => {
  return restClient.post(`/rewards/${path.store}/categories`, body, options);
};

export const getRewardCategory = (
  path: { store: RewardPlaform; rewardCategoryId: Uuid },
  options?: RestRequestOptions,
): Promise<RestResponse<RewardCategoryData>> => {
  return restClient.get(
    `/rewards/${path.store}/categories/${path.rewardCategoryId}`,
    {},
    options,
  );
};

export const updateRewardCategory = (
  path: { store: RewardPlaform; rewardCategoryId: Uuid },
  body: PartialPick<RewardCategoryJson, 'i18nRewardCategoryInfo' | 'ordinal'>,
  options?: RestRequestOptions,
): Promise<RestResponse<RewardCategoryData>> => {
  return restClient.patch(
    `/rewards/${path.store}/categories/${path.rewardCategoryId}`,
    body,
    options,
  );
};

export const deleteRewardCategory = (
  path: { store: RewardPlaform; rewardCategoryId: Uuid },
  params: { relocationCategoryId?: Uuid } = {},
  options?: RestRequestOptions,
): Promise<RestResponse<{ rewardCategory: null }>> => {
  return restClient.delete(
    `/rewards/${path.store}/categories/${path.rewardCategoryId}`,
    params,
    options,
  );
};

interface RewardOffersData {
  rewardOffers: RewardOfferJson[];
  rewardCategories?: RewardCategoryJson[];
}
export const getRewardOffers = <
  P extends {
    offeredTo?: UserId;
    rewardCategoryId?: Uuid;
    leaderboardId?: Uuid;
  } & CursorParams &
    Optional<WithProfilesParams>,
>(
  path: { store: RewardPlaform },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<
  RestResponse<RewardOffersData & CursorData & WithProfilesData<P>>
> => {
  return restClient.get(`/rewards/${path.store}/offers`, params, options);
};

interface RewardOfferData {
  rewardOffer: RewardOfferJson;
}
export const createRewardOffer = (
  path: { store: RewardPlaform },
  body: ModifiedPick<
    ToAttachmentParams<RewardOfferJson>,
    | 'startedAt'
    | 'i18nRewardOfferInfo'
    | 'rewardCategoryId'
    | 'provider'
    | 'reward'
    | 'price'
    | 'quota'
    | 'requirement',
    'endedAt' | 'regions' | 'maxRedemptionsPerUser'
  >,
  options?: RestRequestOptions,
): Promise<RestResponse<RewardOfferData & UploadAttachmentData>> => {
  return restClient.post(`/rewards/${path.store}/offers`, body, options);
};

export const getRewardOffer = (
  path: { store: RewardPlaform; rewardOfferId: Uuid },
  options?: RestRequestOptions,
): Promise<RestResponse<RewardOfferData>> => {
  return restClient.get(
    `/rewards/${path.store}/offers/${path.rewardOfferId}`,
    {},
    options,
  );
};

export const updateRewardOffer = (
  path: { store: RewardPlaform; rewardOfferId: Uuid },
  body: PartialPick<
    ToAttachmentParams<RewardOfferJson>,
    | 'startedAt'
    | 'i18nRewardOfferInfo'
    | 'rewardCategoryId'
    | 'regions'
    | 'provider'
    | 'reward'
    | 'price'
    | 'quota'
    | 'maxRedemptionsPerUser'
  > & { endedAt?: number | null },
  options?: RestRequestOptions,
): Promise<RestResponse<RewardOfferData & UploadAttachmentData>> => {
  return restClient.patch(
    `/rewards/${path.store}/offers/${path.rewardOfferId}`,
    body,
    options,
  );
};

export const deleteRewardOffer = (
  path: { store: RewardPlaform; rewardOfferId: Uuid },
  options?: RestRequestOptions,
): Promise<RestResponse<{ rewardOffer: null }>> => {
  return restClient.delete(
    `/rewards/${path.store}/offers/${path.rewardOfferId}`,
    {},
    options,
  );
};

interface RewardRedemptionsData {
  rewardRedemptions: RewardRedemptionJson[];
  rewardOffers: RewardOfferJson[];
  lastRedemptionWithRecipient?: RewardRedemptionJson;
}
export const getRewardRedemptions = <
  P extends PartialPick<RewardRedemptionJson, 'rewardOfferId'> & {
    state?: RewardRedemptionJson['state'][];
    redeemedBy?: string;
    lastWithRecipient?: boolean;
  } & CursorParams &
    Optional<WithProfilesParams>,
>(
  path: { store: RewardPlaform },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<
  RestResponse<RewardRedemptionsData & CursorData & WithProfilesData<P>>
> => {
  return restClient.get(`/rewards/${path.store}/redemptions`, params, options);
};

interface RewardRedemptionData {
  rewardRedemption: RewardRedemptionJson;
}
export const createRewardRedemption = (
  path: { store: RewardPlaform },
  body: ModifiedPick<
    RewardRedemptionJson,
    'rewardOfferId',
    'recipient' | 'variant' | 'quantity'
  > & {
    email?: string;
    paymentGateway?: 'stripe';
    userCode?: string;
    referralCode?: string;
  },
  options?: RestRequestOptions,
): Promise<RestResponse<RewardRedemptionData>> => {
  return restClient.post(`/rewards/${path.store}/redemptions`, body, options);
};

export const getRewardRedemption = (
  path: { store: RewardPlaform; rewardRedemptionId: Uuid },
  options?: RestRequestOptions,
): Promise<RestResponse<RewardRedemptionData>> => {
  return restClient.get(
    `/rewards/${path.store}/redemptions/${path.rewardRedemptionId}`,
    {},
    options,
  );
};

export const fulfillPhysicalGoodRedemption = (
  path: { store: RewardPlaform; rewardRedemptionId: Uuid },
  body: {
    shippingCompany: string;
    shipmentTrackingNumber: string;
    shipmentTrackingWebsite?: string;
  },
  options?: RestRequestOptions,
): Promise<RestResponse<RewardRedemptionData>> => {
  return restClient.post(
    `/rewards/${path.store}/redemptions/${path.rewardRedemptionId}/fulfillment`,
    body,
    options,
  );
};

export const extendRewardRedemptionCompletion = (
  path: { store: RewardPlaform; rewardRedemptionId: Uuid },
  body: { durationInMs: number },
  options?: RestRequestOptions,
): Promise<RestResponse<RewardRedemptionData>> => {
  return restClient.post(
    `/rewards/${path.store}/redemptions/${path.rewardRedemptionId}/extension`,
    body,
    options,
  );
};

export const completeRewardRedemption = (
  path: { store: RewardPlaform; rewardRedemptionId: Uuid },
  body: { password?: string } = {},
  options?: RestRequestOptions,
): Promise<RestResponse<RewardRedemptionData>> => {
  return restClient.post(
    `/rewards/${path.store}/redemptions/${path.rewardRedemptionId}/completion`,
    body,
    options,
  );
};

export const remindRewardRedemption = (
  path: { store: RewardPlaform; rewardRedemptionId: Uuid },
  options?: RestRequestOptions,
): Promise<RestResponse<RewardRedemptionData>> => {
  return restClient.post(
    `/rewards/${path.store}/redemptions/${path.rewardRedemptionId}/reminder`,
    {},
    options,
  );
};

export const cancelRewardRedemption = (
  path: { store: RewardPlaform; rewardRedemptionId: Uuid },
  options?: RestRequestOptions,
): Promise<RestResponse<RewardRedemptionData>> => {
  return restClient.post(
    `/rewards/${path.store}/redemptions/${path.rewardRedemptionId}/cancellation`,
    {},
    options,
  );
};

type RewardReferralCodeData = {
  rewardReferralCode: RewardReferralCode;
};

export const getRewardReferralCode = (
  path: { referralSystem: ReferralSystem },
  options?: RestRequestOptions,
): Promise<RestResponse<RewardReferralCodeData>> => {
  return restClient.get(
    `/rewards/referrals/${path.referralSystem}/code`,
    {},
    options,
  );
};

type RewardReferralsData = {
  rewardReferrals: RewardReferralJson[];
};

export const getRewardReferrals = <
  P extends {
    referredBy?: UserId;
  } & CursorParams &
    Optional<WithProfilesParams>,
>(
  path: { referralSystem: ReferralSystem },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<
  RestResponse<RewardReferralsData & CursorData & WithProfilesData<P>>
> => {
  return restClient.get(
    `/rewards/referrals/${path.referralSystem}/referrals`,
    params,
    options,
  );
};

interface RewardReferralData {
  rewardReferral: RewardReferralJson | null;
  rewardReferralCode?: RewardReferralCode;
}

export const getRewardReferral = <P extends Optional<WithProfilesParams>>(
  path: { referralSystem: ReferralSystem },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<RewardReferralData & WithProfilesData<P>>> => {
  return restClient.get(
    `/rewards/referrals/${path.referralSystem}/referral`,
    params,
    options,
  );
};

export const getRewardReferralUser = <P extends Optional<WithProfilesParams>>(
  path: { referralSystem: ReferralSystem; userId: Uuid },
  params: P = {} as P,
  options?: RestRequestOptions,
): Promise<RestResponse<(RewardReferralData & WithProfilesData<P>) | null>> => {
  return restClient.get(
    `/rewards/referrals/${path.referralSystem}/referrals/${path.userId}`,
    params,
    options,
  );
};

export interface ReferralCodeValidationData {
  isValid: boolean;
}

export const referralCodeValidation = (
  path: { referralSystem: ReferralSystem },
  body: { referralCode: string },
  options?: RestRequestOptions,
): Promise<RestResponse<ReferralCodeValidationData>> => {
  return restClient.post(
    `/rewards/referrals/${path.referralSystem}/code-validation`,
    body,
    options,
  );
};
